import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, ArrowRight, Sparkles, Ban } from 'lucide-react';

const WelcomePopup = () => {
  const [event, setEvent] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Check if popup was already dismissed this session
    const dismissed = sessionStorage.getItem('popup_dismissed');
    if (dismissed) return;

    // Fetch popup event after a delay
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/popup`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.id) {
            setEvent(data);
            setIsVisible(true);
          }
        }
      } catch (error) {
        console.error('Failed to fetch popup event');
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      sessionStorage.setItem('popup_dismissed', 'true');
    }, 300);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    return `${hours}h${minutes}`;
  };

  if (!isVisible || !event) return null;

  return (
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-opacity duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      onClick={handleClose}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div 
        className={`relative bg-[#FAF9F6] rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden transform transition-all duration-300 ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-600 hover:text-gray-900 transition-colors shadow-sm"
          aria-label="Fermer"
        >
          <X size={18} />
        </button>

        {/* Cover Image */}
        {event.cover_image_url ? (
          <div className="w-full h-48 md:h-56 overflow-hidden relative">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${event.cover_image_url}`}
              alt={event.title}
              className="w-full h-full object-cover"
            />
            {/* Badges on image */}
            <div className="absolute top-3 left-3 flex flex-col gap-2">
              {event.show_nouveau_badge && (
                <span className="flex items-center gap-1 bg-amber-500 text-white text-xs font-medium px-2.5 py-1 rounded-full shadow-sm">
                  <Sparkles size={12} /> Nouveau
                </span>
              )}
              {event.is_sold_out && (
                <span className="flex items-center gap-1 bg-red-600 text-white text-xs font-medium px-2.5 py-1 rounded-full shadow-sm">
                  <Ban size={12} /> Complet
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="p-4 flex gap-2 justify-center">
            {event.show_nouveau_badge && (
              <span className="flex items-center gap-1 bg-amber-500 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                <Sparkles size={12} /> Nouveau
              </span>
            )}
            {event.is_sold_out && (
              <span className="flex items-center gap-1 bg-red-600 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                <Ban size={12} /> Complet
              </span>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-6 md:p-8">
          {/* Gold accent line */}
          <div className="w-16 h-0.5 bg-gradient-to-r from-amber-500 to-amber-300 mb-6" />

          {/* Title */}
          <h2 
            className="text-2xl md:text-3xl text-gray-900 mb-4"
            style={{ fontFamily: 'Playfair Display, serif', fontWeight: '500', lineHeight: '1.3' }}
          >
            {event.title}
          </h2>

          {/* Date & Time */}
          <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-4">
            <span className="flex items-center gap-2">
              <Calendar size={16} className="text-amber-600" />
              <span style={{ fontFamily: 'var(--font-body)' }}>{formatDate(event.event_date)}</span>
            </span>
            <span className="flex items-center gap-2">
              <Clock size={16} className="text-amber-600" />
              <span style={{ fontFamily: 'var(--font-body)' }}>{formatTime(event.event_time)}</span>
            </span>
          </div>

          {/* Price */}
          {event.price_per_person && (
            <div className="mb-4">
              <span 
                className="text-2xl text-gray-900 font-semibold"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {event.price_per_person}
              </span>
              <span className="text-sm text-gray-500 ml-1">par personne</span>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <p 
              className="text-gray-600 mb-6 line-clamp-3"
              style={{ fontFamily: 'var(--font-body)', lineHeight: '1.7' }}
            >
              {event.description}
            </p>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            {event.pdf_url && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}${event.pdf_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 border-2 border-gray-900 text-gray-900 text-sm tracking-wide hover:bg-gray-900 hover:text-white transition-colors"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                Voir le menu
              </a>
            )}
            {event.is_sold_out ? (
              <div
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-gray-300 text-gray-600 text-sm tracking-wide cursor-not-allowed"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                <Ban size={16} />
                Complet
              </div>
            ) : (
              <a
                href="/#reservation"
                onClick={handleClose}
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-gray-900 text-white text-sm tracking-wide hover:bg-gray-800 transition-colors"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                Réserver
                <ArrowRight size={16} />
              </a>
            )}
          </div>
        </div>

        {/* Decorative corner */}
        <div className="absolute bottom-0 right-0 w-24 h-24 overflow-hidden pointer-events-none">
          <div className="absolute -bottom-12 -right-12 w-24 h-24 bg-gradient-to-tl from-amber-100/50 to-transparent rounded-full" />
        </div>
      </div>
    </div>
  );
};

export default WelcomePopup;
